
/**
 * Helper functions for handling calendar events
 */

/**
 * Ensures that event dates are properly formatted for FullCalendar
 * This prevents issues with timestamps being incorrectly assigned
 */
export const createEventDate = (date: Date, hours: number, minutes: number = 0): Date => {
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, 0, 0);
  return newDate;
};

/**
 * Creates a date in the future or past relative to today
 */
export const createRelativeDate = (dayOffset: number, hours: number, minutes: number = 0): Date => {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  return createEventDate(date, hours, minutes);
};

/**
 * Format date according to current view and locale
 */
export const formatViewDate = (
  date: Date | null, 
  view: string, 
  locale: string,
  firstDay: number, 
): string => {
  if (!date) return '';
  
  const options: Intl.DateTimeFormatOptions = {};
  
  if (view === 'timeGridDay' || view === 'dayGridDay') {
    // Day view - full date with weekday
    if (locale === 'fr') {
      options.weekday = 'long';
      options.day = 'numeric';
      options.month = 'short';
      options.year = 'numeric';
    } else {
      // English format
      options.weekday = 'long';
      options.month = 'short';
      options.day = 'numeric';
      options.year = 'numeric';
    }
  } else if (view === 'timeGridWeek' || view === 'dayGridWeek') {
    // Week view - date range
    const startOfWeek = new Date(date);
    const endOfWeek = new Date(date);
    
    // Adjust to get the correct week range based on firstDay (0 for Sunday, 1 for Monday)
    const day = date.getDay();
    let diff = date.getDate() - day + (firstDay === 0 ? 0 : 1); // Adjust for first day of the week
    if (firstDay === 0 && day === 0) {
      diff -= 7;  // If it's Sunday, start the week from last Sunday
    }

    startOfWeek.setDate(diff);
    endOfWeek.setDate(diff + 6);
    
    if (locale === 'fr') {
      // French format: "16 - 22 Mars 2025"
      const startDay = startOfWeek.getDate();
      const endDay = endOfWeek.getDate();
      const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(endOfWeek);
      const year = endOfWeek.getFullYear();
      
      return `${startDay} – ${endDay} ${month} ${year}`;
    } else {
      // English format: "March 16 - 22, 2025"
      const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(startOfWeek);
      const startDay = startOfWeek.getDate();
      const endDay = endOfWeek.getDate();
      const year = endOfWeek.getFullYear();
      
      return `${month} ${startDay} – ${endDay}, ${year}`;
    }
  } else if (view === 'listView' || view === 'listWeek') {
    // List view - show current month and year
    if (locale === 'fr') {
      const startOfWeek = new Date(date);
      const endOfWeek = new Date(date);
      
      const day = date.getDay();
      let diff = date.getDate() - day + (firstDay === 0 ? 0 : 1);
      if (firstDay === 0 && day === 0) {
        diff -= 7;  // If it's Sunday, start the week from last Sunday
      }
      
      startOfWeek.setDate(diff);
      endOfWeek.setDate(diff + 6);
      
      const startDay = startOfWeek.getDate();
      const endDay = endOfWeek.getDate();
      const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(endOfWeek);
      const year = endOfWeek.getFullYear();
      
      return `${startDay} – ${endDay} ${month} ${year}`;
    } else {
      const startOfWeek = new Date(date);
      const endOfWeek = new Date(date);
      
      const day = date.getDay();
      let diff = date.getDate() - day + (firstDay === 0 ? 0 : 1);
      if (firstDay === 0 && day === 0) {
        diff -= 7;  // If it's Sunday, start the week from last Sunday
      }
      
      startOfWeek.setDate(diff);
      endOfWeek.setDate(diff + 6);
      
      const month = new Intl.DateTimeFormat('en', { month: 'long' }).format(startOfWeek);
      const startDay = startOfWeek.getDate();
      const endDay = endOfWeek.getDate();
      const year = endOfWeek.getFullYear();
      
      return `${month} ${startDay} – ${endDay}, ${year}`;
    }
  } else if (view === 'dayGridMonth') {
    // Month view - just month and year
    options.month = 'long';
    options.year = 'numeric';
  } else if (view === 'multiMonthYear' || view === 'listYear') {
    // Year view - just year
    return date.getFullYear().toString();
  }
  
  return new Intl.DateTimeFormat(locale, options).format(date);
};
